/**
 * performanceUtils
 *
 * Helpers to monitor and audit client-side performance (scroll FPS, audit of
 * heavy elements, smooth scroll tester). Auto-exposes helpers in dev.
 */

// Simple scroll performance monitor
export const monitorScrollPerformance = () => {
  let frameCount = 0;
  let lastTime = performance.now();
  let fps = 0;
  
  const monitor = () => {
    const currentTime = performance.now();
    frameCount++;
    
    if (currentTime - lastTime >= 1000) {
      fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
      console.log(`🎯 Scroll FPS: ${fps} (Target: 60fps, Good: >50fps, Poor: <30fps)`);
      frameCount = 0;
      lastTime = currentTime;
    }
    
    requestAnimationFrame(monitor);
  };
  
  console.log('📊 Starting scroll performance monitor...');
  console.log('💡 Scroll the page to see real-time FPS metrics in the console');
  requestAnimationFrame(monitor);
  
  // Return a cleanup function
  return () => {
    console.log('📊 Scroll performance monitoring stopped');
  };
};

// Check for performance-heavy elements
export const auditPagePerformance = () => {
  console.log('🔍 Page Performance Audit:');
  
  // Check for heavy animations
  const animatedElements = document.querySelectorAll('[style*="animation"], .animate-');
  console.log(`🎭 Animated elements: ${animatedElements.length}`);
  
  // Check for blur effects
  const blurElements = document.querySelectorAll('[class*="blur-"], [style*="blur"]');
  console.log(`💨 Blur effects: ${blurElements.length}`);
  
  // Check for video elements
  const videos = document.querySelectorAll('video');
  console.log(`🎥 Video elements: ${videos.length}`);
  
  // Check for large images
  const images = document.querySelectorAll('img');
  let largeImages = 0;
  images.forEach(img => {
    if (img.naturalWidth > 1920 || img.naturalHeight > 1080) {
      largeImages++;
    }
  });
  console.log(`🖼️ Large images (>1920x1080): ${largeImages}/${images.length}`);
  
  // Safari-specific recommendations
  if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
    console.log('🍎 Safari detected - Performance tips:');
    console.log('  • Reduce blur effects (blur-sm instead of blur-3xl)');
    console.log('  • Limit concurrent animations');
    console.log('  • Use transform: translateZ(0) for hardware acceleration');
    console.log('  • Batch video preloading');
  }
};

// Simple function to test scroll smoothness
export const testScrollSmoothnessTo = (elementId) => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with ID "${elementId}" not found`);
    return;
  }
  
  console.log(`🚀 Testing smooth scroll to ${elementId}...`);
  
  const startTime = performance.now();
  
  element.scrollIntoView({ 
    behavior: 'smooth',
    block: 'start'
  });
  
  // Monitor the scroll completion
  const checkComplete = () => {
    if (Math.abs(element.getBoundingClientRect().top) < 10) {
      const endTime = performance.now();
      console.log(`✅ Smooth scroll completed in ${Math.round(endTime - startTime)}ms`);
    } else {
      requestAnimationFrame(checkComplete);
    }
  };
  
  requestAnimationFrame(checkComplete);
};

// Add performance monitoring to window for easy access in console
if (typeof window !== 'undefined') {
  window.monitorScrollPerformance = monitorScrollPerformance;
  window.auditPagePerformance = auditPagePerformance;
  window.testScrollSmoothnessTo = testScrollSmoothnessTo;
  
  // Auto-run audit on page load for development
  if (import.meta.env.DEV) {
    window.addEventListener('load', () => {
      setTimeout(auditPagePerformance, 1000);
    });
  }
} 